import React from "react"
import { useTheme } from "@chakra-ui/react"
import TripsMapKey from "./TripsMapKey"
import TripLayerGoogleMap from "./TripLayerGoogleMap"
import GMap from "../gmaps/GMap"
import { TripsMapProps } from "./TripsMapMapbox"
import { LatLngObj } from "../lib/helpers/googleMap"
import Polyline from "../gmaps/Polyline"
import ZoneLayer from "../gmaps/ZoneLayer"
import { useGoogleMapsInitBounds } from "../lib/hooks/useGoogleMapsInitBounds"
import { useFillOpacity } from "../lib/hooks/useDropZoneStyle"
import { useTripsColorObj } from "../lib/hooks/useTripsStyle"

const MAP_ID = "trips-gmap"

const TripsMapGoogleMap: React.FC<TripsMapProps> = ({
  bbox,
  trips,
  selectedTrip,
  onSelectTrip,
  showServiceZones,
  serviceZones,
  showDropzones,
  dropzones,
  showMaintenanceZones,
  maintenanceZones,
}) => {
  const theme = useTheme()
  const initialBounds = useGoogleMapsInitBounds(bbox)
  const polygonsFillOpacity = useFillOpacity()
  const tripColor = useTripsColorObj()

  const gaps = React.useMemo(() => {
    return trips
      .filter((t) => !!t.route)
      .reduce((acc, curr, index) => {
        const nextIndex = index + 1
        const hasNext = nextIndex !== trips.length
        if (hasNext && !!trips[nextIndex].route) {
          const lastOne = trips[nextIndex].route!.coordinates.length - 1
          const firstNode = {
            lng: curr.route!.coordinates[0][0],
            lat: curr.route!.coordinates[0][1],
          } as LatLngObj
          const lastNode = {
            lng: trips[nextIndex].route!.coordinates[lastOne][0],
            lat: trips[nextIndex].route!.coordinates[lastOne][1],
          } as LatLngObj
          return [[firstNode, lastNode], ...acc]
        }
        return acc
      }, [] as LatLngObj[][])
  }, [trips])

  return (
    <GMap
      mapId={MAP_ID}
      containerStyle={{
        height: "100%",
        width: "100%",
      }}
      initialBounds={initialBounds}
      zoomControl={false}
      // onClick={() => onSelectTrip(undefined)}
    >
      <TripsMapKey />

      {/* dropzones */}
      <ZoneLayer
        mapId={MAP_ID}
        zones={dropzones}
        showZones={showDropzones}
        properties={{
          zoneType: "dropzone",
        }}
        layerStyle={{
          fillColor: theme.colors.blue["500"],
          strokeColor: theme.colors.blue["800"],
          strokeWeight: 1,
          fillOpacity: polygonsFillOpacity,
          zIndex: 30,
        }}
      />

      {/* cycling_area zones (aka serviceZones) */}
      <ZoneLayer
        mapId={MAP_ID}
        zones={serviceZones}
        showZones={showServiceZones}
        properties={{
          zoneType: "cycling_area",
        }}
        layerStyle={{
          fillColor: theme.colors.purple["500"],
          strokeColor: theme.colors.purple["800"],
          strokeWeight: 1,
          fillOpacity: polygonsFillOpacity,
          zIndex: 30,
        }}
      />

      {/* maintenance zones */}
      <ZoneLayer
        mapId={MAP_ID}
        zones={maintenanceZones}
        showZones={showMaintenanceZones}
        properties={{
          zoneType: "maintenance",
        }}
        layerStyle={{
          fillColor: theme.colors.gray["500"],
          strokeColor: theme.colors.gray["800"],
          strokeWeight: 1,
          fillOpacity: polygonsFillOpacity,
          zIndex: 30,
        }}
      />

      {/* GAPS */}
      {gaps?.map((gap, idx) => (
        <Polyline
          key={`gap_${idx}`}
          mapId={MAP_ID}
          path={gap}
          lineStyle="dashed"
          strokeOptions={{
            strokeColor: tripColor["500"],
            strokeOpacity: 0.7,
            strokeWeight: 5,
          }}
          zOptions={{
            zIndex: 40,
          }}
          addBorder
          borderColor={tripColor["900"]}
        />
      ))}

      {/* TRIPS */}
      {trips.map((trip) => (
        <TripLayerGoogleMap
          key={trip.uuid}
          mapId={MAP_ID}
          trip={trip}
          onSelectTrip={onSelectTrip}
          isSelected={selectedTrip?.uuid === trip.uuid || false}
          zIndex={40}
        />
      ))}
    </GMap>
  )
}

export default TripsMapGoogleMap
